import { useState } from 'react';
import { X, AlertTriangle, CheckCircle2, Calendar, Clock, FileText, UserCheck, BadgeCheck, MessageSquare } from 'lucide-react';
import type { Lead } from './LeadCard';
import type { StageRequirement, LeadStatus } from './stageValidation';
import { statusLabels } from './stageValidation';

interface StageRequirementsModalProps {
  lead: Lead;
  targetStatus: LeadStatus;
  missingRequirements: StageRequirement[];
  onClose: () => void;
  onSaveAndMove: (updates: Partial<Lead>, addActivity?: { type: 'call' | 'email' | 'meeting' | 'note'; content: string }) => void;
  onOpenFullEdit: () => void;
}

const requirementIcons: Record<string, React.ReactNode> = {
  pain_point: <FileText className="w-4 h-4" />,
  demo_date: <Calendar className="w-4 h-4" />,
  demo_time: <Clock className="w-4 h-4" />,
  is_decision_maker: <UserCheck className="w-4 h-4" />,
  is_qualified: <BadgeCheck className="w-4 h-4" />,
  activity_count: <MessageSquare className="w-4 h-4" />,
  meeting_activity: <MessageSquare className="w-4 h-4" />,
};

export default function StageRequirementsModal({
  lead,
  targetStatus,
  missingRequirements,
  onClose,
  onSaveAndMove,
  onOpenFullEdit,
}: StageRequirementsModalProps) {
  const [formData, setFormData] = useState({
    pain_point: lead.pain_point || '',
    demo_date: lead.demo_date || '',
    demo_time: lead.demo_time || '',
    is_decision_maker: Boolean(lead.is_decision_maker),
    is_qualified: Boolean(lead.is_qualified),
  });

  const [activityType, setActivityType] = useState<'call' | 'email' | 'meeting'>('call');
  const [activityContent, setActivityContent] = useState('');
  const [saving, setSaving] = useState(false);

  const needsMeetingActivity = missingRequirements.some(r => r.activityType === 'meeting');

  const handleSubmit = async () => {
    setSaving(true);

    const updates: Partial<Lead> = {};
    let addActivity: { type: 'call' | 'email' | 'meeting' | 'note'; content: string } | undefined;

    // Collect field updates
    for (const req of missingRequirements) {
      if (req.type === 'field') {
        const value = formData[req.field as keyof typeof formData];
        if (value) {
          (updates as Record<string, unknown>)[req.field] = value;
        }
      } else if (req.type === 'checkbox') {
        const value = formData[req.field as keyof typeof formData];
        (updates as Record<string, unknown>)[req.field] = value ? 1 : 0;
      } else if (req.type === 'activity' && activityContent.trim()) {
        addActivity = {
          type: needsMeetingActivity ? 'meeting' : activityType,
          content: activityContent.trim(),
        };
      }
    }

    await onSaveAndMove(updates, addActivity);
    setSaving(false);
  };

  const canSubmit = () => {
    for (const req of missingRequirements) {
      if (req.type === 'field') {
        const value = formData[req.field as keyof typeof formData];
        if (!value) return false;
      } else if (req.type === 'checkbox') {
        const value = formData[req.field as keyof typeof formData];
        if (!value) return false;
      } else if (req.type === 'activity') {
        if (!activityContent.trim()) return false;
      }
    }
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-800 dark:text-stone-100">
                Missing Information
              </h3>
              <p className="text-sm text-stone-500">
                Required to move to {statusLabels[targetStatus]}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Please fill in the following for <strong>{lead.contact_name}</strong>:
          </p>

          {missingRequirements.map((req) => (
            <div key={req.field} className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700 dark:text-stone-300">
                {requirementIcons[req.field] || <CheckCircle2 className="w-4 h-4" />}
                {req.label}
              </label>

              {req.type === 'field' && req.field === 'pain_point' && (
                <textarea
                  value={formData.pain_point}
                  onChange={(e) => setFormData({ ...formData, pain_point: e.target.value })}
                  placeholder="What problem are they trying to solve?"
                  rows={2}
                  className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
              )}

              {req.type === 'field' && req.field === 'demo_date' && (
                <input
                  type="date"
                  value={formData.demo_date}
                  onChange={(e) => setFormData({ ...formData, demo_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
              )}

              {req.type === 'field' && req.field === 'demo_time' && (
                <input
                  type="time"
                  value={formData.demo_time}
                  onChange={(e) => setFormData({ ...formData, demo_time: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
              )}

              {req.type === 'checkbox' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData[req.field as keyof typeof formData] as boolean}
                    onChange={(e) => setFormData({ ...formData, [req.field]: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                  />
                  <span className="text-sm text-stone-600 dark:text-stone-400">
                    Yes, confirmed
                  </span>
                </label>
              )}

              {req.type === 'activity' && (
                <div className="space-y-2">
                  {!needsMeetingActivity && (
                    <select
                      value={activityType}
                      onChange={(e) => setActivityType(e.target.value as 'call' | 'email' | 'meeting')}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                    >
                      <option value="call">Phone Call</option>
                      <option value="email">Email</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  )}
                  <textarea
                    value={activityContent}
                    onChange={(e) => setActivityContent(e.target.value)}
                    placeholder={needsMeetingActivity ? "Describe the demo meeting..." : "What happened? (e.g., 'Discussed pricing options')"}
                    rows={2}
                    className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 dark:border-stone-800 flex items-center justify-between gap-3">
          <button
            onClick={onOpenFullEdit}
            className="text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
          >
            Open full editor
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit() || saving}
              className="px-4 py-2 text-sm font-medium bg-sage text-white rounded-lg hover:bg-sage/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>Save & Move</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
